import React, { useState } from 'react';

import {
  Input,
  MultiSelectButton,
  TableCell,
  TableHeaderCell,
} from '@hcs/design-system';
import {
  PropertyStateArgsProps,
  PropertyStateEditCallbackArgs,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStateFilterProps,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import { formatNumber, numInputStrStrip } from '@hcs/utils';

const FIELD = PropertyStateFields.stories;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Stories';
const labelShort = 'Stories';
const propertyStatePath: PropertyStatePaths = '/propertyDetails/storiesNumber';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  } else if (propertyStateType === 'Core') {
    return propertyState?.propertyDetails?.storiesNumber;
  }
  return undefined;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatNumber(getValue(propertyStateArgs));

// For displaying multiple fields in a single line
const labelMicro: FieldConfig['labelMicro'] = 'Stories';
const formatValueShort: FieldConfig['formatValueShort'] = (propertyStateArgs) =>
  formatNumber(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${formatValue(propertyStateArgs)}`;
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};
// eslint-disable-next-line react-refresh/only-export-components
const Edit = ({
  propertyStateArgs,
  onChange,
  className,
}: PropertyStateArgsProps & {
  onChange: (args: PropertyStateEditCallbackArgs<typeof FIELD>) => void;
  className?: string;
}) => {
  const defaultValue = getValue(propertyStateArgs);
  const [value, setValue] = useState(
    defaultValue == null ? null : defaultValue.toString(),
  );

  return (
    <Input
      dataHcName="stories-adjust-input"
      ariaLabel={`Adjust ${label}`}
      className={className}
      value={value?.toString() || ''}
      onChange={(newValue) => {
        const valueOrNull = newValue === '' ? null : numInputStrStrip(newValue);
        setValue(valueOrNull);

        onChange({
          path: propertyStatePath,
          value: valueOrNull === null ? valueOrNull : Number(valueOrNull),
          field: FIELD,
        });
      }}
      maxLength={2}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

const FILTER_OPTIONS = [
  {
    label: 'Any',
    value: null,
  },
  {
    label: 'Single Story',
    value: 'single',
  },
  {
    label: 'Multi',
    value: 'multi',
  },
];

const getStoryValues = (value: string | null) =>
  value === 'single'
    ? { minStories: 1, maxStories: 1 }
    : value === 'multi'
      ? { minStories: 2, maxStories: undefined }
      : { minStories: undefined, maxStories: undefined };

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
}: PropertyStateFilterProps<typeof FIELD>) => {
  const value =
    spatialFiltersInput.minStories != null && spatialFiltersInput.minStories > 1
      ? 'multi'
      : spatialFiltersInput.maxStories === 1
        ? 'single'
        : null;

  return (
    <MultiSelectButton
      dataHcName={`${FIELD}-filter`}
      value={value}
      options={FILTER_OPTIONS}
      disabled={disabled}
      singleSelect={true}
      className={className}
      onChange={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            ...spatialFiltersInput,
            ...getStoryValues(value),
          },
        });
      }}
    />
  );
};

export const STORIES_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  labelMicro,
  formatValueShort,
  formatInline,
  HeaderCell,
  ContentCell,
  Filter,
  Display,
  Edit,
};
